import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { useImageContext } from "../../../contexts/ImageContext";
import { clearEditorSession } from "../../../state/editorStore";
import "./ManageAssets.css";
import "../../../components/manage/GalleryCardCheckbox.css";
import logger from "../../../utils/logger";
import {
  scaleFitIcon,
  scaleOriginalIcon,
  downloadNavIcon as downloadIcon,
  deleteNavIcon as deleteIcon,
} from "../../../icons/assets";
import {
  getCreations,
  mergeDesignItems,
  MY_DESIGNS_FILE_TYPES,
  MY_DESIGNS_PAGE_SIZE,
  fetchCreationImageBlob,
  fetchExportImageBlob,
  downloadCreationAttachment,
  downloadExportAttachment,
  deleteCreation,
  deleteExport,
} from "../../../services/api/editor/background_removal";
import { saveBlobAsFile } from "../../../services/api/editor/export";
import GalleryLoadMore from "../../../components/manage/GalleryLoadMore";
import GalleryActionConfirmModal from "../../../components/manage/GalleryActionConfirmModal";
import RoundLoader from "../../../components/ui/RoundLoader";
import FadeInMedia from "../../../components/ui/FadeInMedia";
import { withAuthReadyRequest } from "../../../utils/withAuthReadyRequest";

function designNumericId(design) {
  if (design.kind === "export") {
    return design.export_id ?? Number(String(design.id).replace(/^export:/, ""));
  }
  return design.creation_id ?? Number(String(design.id).replace(/^creation:/, ""));
}

function designDownloadFileName(design) {
  const ext = design.format || design.output_format || design.file_ext || "png";
  const rawName =
    design.file_name ||
    `${design.kind}-${designNumericId(design) || "design"}`;
  const baseName = rawName.replace(/\.[^./\\]+$/, "");
  return `${baseName}.${ext}`;
}

function groupDesignsByDate(designsList) {
  const grouped = {};
  designsList.forEach((design) => {
    const date = new Date(design.created_at).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
    });
    if (!grouped[date]) {
      grouped[date] = [];
    }
    grouped[date].push(design);
  });
  return grouped;
}

const IMAGE_LOAD_CONCURRENCY = 4;
let imageLoadActive = 0;
const imageLoadQueue = [];

function runWithImageLoadLimit(task) {
  return new Promise((resolve, reject) => {
    const run = () => {
      imageLoadActive += 1;
      Promise.resolve()
        .then(task)
        .then(resolve, reject)
        .finally(() => {
          imageLoadActive -= 1;
          const next = imageLoadQueue.shift();
          if (next) next();
        });
    };
    if (imageLoadActive < IMAGE_LOAD_CONCURRENCY) {
      run();
    } else {
      imageLoadQueue.push(run);
    }
  });
}

function DesignCardImage({ design, getToken, scaleMode }) {
  const cardRef = useRef(null);
  const [src, setSrc] = useState(null);
  const [preferBlob, setPreferBlob] = useState(!design.output_url);
  const [exhausted, setExhausted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    let observer = null;

    setExhausted(false);

    if (design.output_url && !preferBlob) {
      setSrc(design.output_url);
      return undefined;
    }

    const loadImage = () =>
      runWithImageLoadLimit(async () => {
        const token = await getToken();
        if (!token || cancelled) {
          if (!cancelled) setExhausted(true);
          return;
        }

        const blob =
          design.kind === "export"
            ? await fetchExportImageBlob(token, designNumericId(design))
            : await fetchCreationImageBlob(token, designNumericId(design));

        if (cancelled) return;
        if (blob && blob.type && blob.type.includes("json")) {
          throw new Error("Design image API returned JSON error");
        }
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      }).catch((err) => {
        if (!cancelled) {
          logger.error("Failed to load design card image", err);
          setExhausted(true);
        }
      });

    const node = cardRef.current;
    if (!node) {
      loadImage();
      return () => {
        cancelled = true;
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      };
    }

    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer?.disconnect();
          loadImage();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(node);

    return () => {
      cancelled = true;
      observer?.disconnect();
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [design, getToken, preferBlob]);

  return (
    <div ref={cardRef} className="design-card__media">
      <FadeInMedia
        src={src}
        alt={design.file_type || "Design"}
        objectFit={scaleMode}
        loaderSize="sm"
        waiting={!exhausted}
        className={
          design.has_transparency ? "design-card__img--transparent" : ""
        }
        onError={() => {
          if (design.output_url && !preferBlob) {
            setPreferBlob(true);
            setSrc(null);
            return;
          }
          setExhausted(true);
        }}
      />
    </div>
  );
}

function appendGroupedDesigns(previousGrouped, newItems) {
  const seen = new Set();
  const flat = [];
  Object.values(previousGrouped)
    .flat()
    .forEach((design) => {
      if (!seen.has(design.id)) {
        seen.add(design.id);
        flat.push(design);
      }
    });
  newItems.forEach((design) => {
    if (!seen.has(design.id)) {
      seen.add(design.id);
      flat.push(design);
    }
  });
  return groupDesignsByDate(flat);
}

const ManageAssets = () => {
  const navigate = useNavigate();
  const { clearImage, setNormalEntry } = useImageContext();
  const [designs, setDesigns] = useState({});
  const [selectedDesigns, setSelectedDesigns] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [sliderValue, setSliderValue] = useState(50);
  const [scaleMode, setScaleMode] = useState("cover");
  const [actionModal, setActionModal] = useState({
    open: false,
    mode: "delete",
    phase: "confirm",
    progress: 0,
    processed: 0,
    itemCount: 0,
    error: null,
  });
  const actionBusyRef = useRef(false);
  const { getToken } = useAuth();

  const imageSize = 200 + sliderValue * 2;

  const fetchDesignsPage = useCallback(
    async (skip, { append = false } = {}) => {
      const data = await withAuthReadyRequest(getToken, (token) =>
        getCreations(token, {
          skip,
          limit: MY_DESIGNS_PAGE_SIZE,
          fileTypes: MY_DESIGNS_FILE_TYPES,
          includeExports: true,
        })
      );
      // Match ImagePicker “My Designs”: keep creations/exports; hide library-only uploads.
      // Do not require output_url — cards can load via creation/export image blob APIs.
      const items = mergeDesignItems(data).filter(
        (item) => item?.file_type !== "library_upload"
      );

      if (append) {
        setDesigns((prev) => appendGroupedDesigns(prev, items));
        setLoadedCount((prev) => prev + items.length);
      } else {
        setDesigns(groupDesignsByDate(items));
        setLoadedCount(items.length);
      }

      setTotalCount(data.total_count ?? items.length);
      setHasMore(Boolean(data.has_more));
    },
    [getToken]
  );

  const loadDesigns = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      await fetchDesignsPage(0, { append: false });
    } catch (error) {
      logger.error("Failed to fetch designs", error);
      setDesigns({});
      setLoadedCount(0);
      setTotalCount(0);
      setHasMore(false);
      setLoadError("Couldn’t load designs. Please refresh and try again.");
    } finally {
      setIsLoading(false);
    }
  }, [fetchDesignsPage]);

  const loadMoreDesigns = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    try {
      setIsLoadingMore(true);
      await fetchDesignsPage(loadedCount, { append: true });
    } catch (error) {
      logger.error("Failed to load more designs", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [fetchDesignsPage, hasMore, isLoadingMore, loadedCount]);

  useEffect(() => {
    loadDesigns();
  }, [loadDesigns]);

  const handleDesignSelect = (designId, event) => {
    event.stopPropagation();
    const newSelected = new Set(selectedDesigns);
    if (newSelected.has(designId)) {
      newSelected.delete(designId);
    } else {
      newSelected.add(designId);
    }
    setSelectedDesigns(newSelected);
  };

  const handleSelectDateGroup = (designsList, isSelected) => {
    const newSelected = new Set(selectedDesigns);
    designsList.forEach((design) => {
      if (isSelected) {
        newSelected.add(design.id);
      } else {
        newSelected.delete(design.id);
      }
    });
    setSelectedDesigns(newSelected);
  };

  const handleOpenDesign = (design) => {
    clearEditorSession();
    setNormalEntry();
    clearImage();
    if (design.kind === "export") {
      navigate(`/editor?exportId=${designNumericId(design)}`);
      return;
    }
    navigate(`/editor?creationId=${designNumericId(design)}`);
  };

  const openActionModal = (mode) => {
    if (selectedDesigns.size === 0) return;
    actionBusyRef.current = false;
    setActionModal({
      open: true,
      mode,
      phase: "confirm",
      progress: 0,
      processed: 0,
      itemCount: selectedDesigns.size,
      error: null,
    });
  };

  const closeActionModal = useCallback(() => {
    setActionModal((prev) => {
      if (prev.phase === "working") return prev;
      return { ...prev, open: false, error: null };
    });
  }, []);

  const confirmGalleryAction = async () => {
    if (actionBusyRef.current) return;
    const mode = actionModal.mode;
    const selectedIds = Array.from(selectedDesigns);
    if (selectedIds.length === 0) return;

    actionBusyRef.current = true;
    setActionModal((prev) => ({
      ...prev,
      phase: "working",
      progress: 6,
      processed: 0,
      error: null,
    }));

    try {
      const token = await getToken();
      if (!token) {
        throw new Error("Please sign in again to continue.");
      }

      const flat = Object.values(designs).flat();
      const items = selectedIds
        .map((id) => flat.find((design) => design.id === id))
        .filter(Boolean);
      if (items.length === 0) {
        throw new Error("Selected designs are no longer available.");
      }
      const total = items.length;

      for (let i = 0; i < items.length; i += 1) {
        const cap = ((i + 0.88) / total) * 100;
        setActionModal((prev) => ({
          ...prev,
          processed: i,
          progress: Math.max(prev.progress, (i / total) * 100 + 6),
        }));

        const creep = window.setInterval(() => {
          setActionModal((prev) => {
            if (prev.phase !== "working" || prev.progress >= cap) return prev;
            return { ...prev, progress: Math.min(prev.progress + 3, cap) };
          });
        }, 140);

        try {
          const design = items[i];
          if (mode === "delete") {
            if (design.kind === "export") {
              await deleteExport(token, designNumericId(design));
            } else {
              await deleteCreation(token, designNumericId(design));
            }
          } else {
            const blob =
              design.kind === "export"
                ? await downloadExportAttachment(token, designNumericId(design))
                : await downloadCreationAttachment(token, designNumericId(design));
            saveBlobAsFile(blob, designDownloadFileName(design));
          }
        } finally {
          window.clearInterval(creep);
        }

        setActionModal((prev) => ({
          ...prev,
          processed: i + 1,
          progress: ((i + 1) / total) * 100,
        }));
      }

      setActionModal((prev) => ({
        ...prev,
        phase: "done",
        progress: 100,
        processed: items.length,
      }));

      if (mode === "delete") {
        setSelectedDesigns(new Set());
        try {
          await fetchDesignsPage(0, { append: false });
        } catch (refreshError) {
          logger.error("Failed to refresh designs after delete", refreshError);
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 900));
      setActionModal((prev) => {
        if (prev.phase !== "done") return prev;
        return { ...prev, open: false };
      });
    } catch (error) {
      logger.error(
        mode === "delete" ? "Failed to delete designs" : "Failed to download designs",
        error
      );
      setActionModal((prev) => ({
        ...prev,
        phase: "error",
        error:
          mode === "delete"
            ? "Failed to delete the selected designs. Please try again."
            : "Failed to download the selected designs. Please try again.",
      }));
    } finally {
      actionBusyRef.current = false;
    }
  };

  if (isLoading) {
    return (
      <div className="manage-assets-wrapper">
        <div className="manage-assets-loading">
          <RoundLoader size="lg" label="Loading designs…" />
        </div>
      </div>
    );
  }

  const hasDesigns = Object.keys(designs).length > 0;

  return (
    <div className="manage-assets-wrapper">
      <div className="manage-assets-header">
        <h1>My Designs</h1>
        <div className="header-controls" title="Controls">
          <button
            className="control-icon scale-toggle-btn"
            title={scaleMode === "contain" ? "Scale to Fit" : "Scale Original"}
            onClick={() =>
              setScaleMode(scaleMode === "contain" ? "cover" : "contain")
            }
          >
            <img
              src={scaleMode === "contain" ? scaleFitIcon : scaleOriginalIcon}
              alt={scaleMode === "contain" ? "Fit" : "Original"}
              style={{ width: "30px", height: "30px" }}
            />
          </button>
          <div className="slider-container" title="Image Size Controller">
            <input
              type="range"
              min="0"
              max="100"
              value={sliderValue}
              onChange={(e) => setSliderValue(Number(e.target.value))}
              className="volume-slider"
              style={{
                background: `linear-gradient(to right, #0066ff 0%, #0066ff ${sliderValue}%, #333 ${sliderValue}%, #333 100%)`,
              }}
            />
          </div>
        </div>
      </div>

      <div className="manage-assets-main">
        {hasDesigns ? (
          Object.entries(designs).map(([date, designsList]) => (
            <div key={date} className="designs-section">
              <div className="designs-date-header">
                <input
                  type="checkbox"
                  className="date-checkbox"
                  onChange={(e) =>
                    handleSelectDateGroup(designsList, e.target.checked)
                  }
                  checked={designsList.every((d) => selectedDesigns.has(d.id))}
                />
                <span className="date-label">{date}</span>
              </div>

              <div
                className="designs-grid"
                style={{
                  gridTemplateColumns: `repeat(auto-fill, minmax(${imageSize}px, ${imageSize}px))`,
                }}
              >
                {designsList.map((design) => (
                  <div
                    key={design.id}
                    className={`design-card ${
                      selectedDesigns.has(design.id) ? "selected" : ""
                    } ${design.has_transparency ? "design-card--transparent" : ""}`}
                    onClick={() => handleOpenDesign(design)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleOpenDesign(design);
                      }
                    }}
                  >
                    <DesignCardImage
                      design={design}
                      getToken={getToken}
                      scaleMode={scaleMode}
                    />
                    <div className="design-overlay">
                      <input
                        type="checkbox"
                        checked={selectedDesigns.has(design.id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => handleDesignSelect(design.id, e)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="no-designs">
            <p>{loadError || "No designs yet. Start creating!"}</p>
          </div>
        )}

        {hasDesigns && (
          <GalleryLoadMore
            loadedCount={loadedCount}
            totalCount={totalCount}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            onLoadMore={loadMoreDesigns}
          />
        )}
      </div>

      <div
        className={`manage-assets-actions${
          selectedDesigns.size > 0 ? " actions-visible" : ""
        }`}
        style={selectedDesigns.size === 0 ? { pointerEvents: "none" } : {}}
      >
        <button className="action-btn download-btn" onClick={() => openActionModal("download")}>
          <img
            src={downloadIcon}
            alt="Download"
            className="action-btn-icon action-btn-icon--download"
            aria-hidden="true"
          />
          Download
        </button>
        <button className="action-btn manage-delete-btn" onClick={() => openActionModal("delete")}>
          <img
            src={deleteIcon}
            alt="Delete"
            className="action-btn-icon action-btn-icon--delete"
            aria-hidden="true"
          />
          Delete
        </button>
      </div>

      <GalleryActionConfirmModal
        open={actionModal.open}
        mode={actionModal.mode}
        itemCount={actionModal.itemCount || selectedDesigns.size}
        itemNoun="design"
        phase={actionModal.phase}
        progress={actionModal.progress}
        processed={actionModal.processed}
        error={actionModal.error}
        onConfirm={confirmGalleryAction}
        onClose={closeActionModal}
      />
    </div>
  );
};

export default ManageAssets;
